// FileUploadForm.jsx

// Single main photo upload (simplified until PMS integration)
import React, { useEffect } from 'react';
import { Grid, Button, Typography, Paper, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { UploadPropertyPageStyles } from './UploadPropertyPage.styles';

const FileUploadForm = ({ images, setImages }) => {
  // Handle single main photo upload
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clean up any existing image preview
    if (images?.length > 0 && images[0]?.preview) {
      URL.revokeObjectURL(images[0].preview);
    }

    // Set the single main photo
    setImages([{
      file,
      preview: URL.createObjectURL(file),
    }]);
  };

  // Remove the main photo
  const handleRemoveImage = () => {
    if (images?.length > 0 && images[0]?.preview) {
      URL.revokeObjectURL(images[0].preview);
    }
    setImages([]);
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (images?.length > 0 && images[0]?.preview) {
        URL.revokeObjectURL(images[0].preview);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasImage = images?.length > 0;
  const imageSrc = hasImage
    ? (typeof images[0] === 'string' ? images[0] : images[0]?.preview || '')
    : '';

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
          Main Photo
        </Typography>
        
        {!hasImage ? (
          // Upload button when no image
          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCameraIcon />}
            sx={{
              ...UploadPropertyPageStyles.uploadButton,
              py: 2,
              borderStyle: 'dashed',
            }}
          >
            Upload Main Photo
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
        ) : (
          // Show the uploaded image with option to change/remove
          <Box sx={{ maxWidth: 400 }}>
            <Paper
              sx={{
                ...UploadPropertyPageStyles.imagePreview,
                position: 'relative',
                border: '2px solid #81c241',
                overflow: 'hidden',
              }}
            >
              {/* Remove button */}
              <IconButton
                onClick={handleRemoveImage}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  ':hover': {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                  },
                  zIndex: 1,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              {/* Main label */}
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  backgroundColor: '#81c241',
                  color: '#fff',
                  padding: '4px 8px',
                  borderTopRightRadius: '4px',
                  fontWeight: 600,
                  zIndex: 1,
                }}
              >
                Main Photo
              </Typography>

              {/* Image preview */}
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt="Main property"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              ) : (
                <div style={{ padding: 16, color: '#888' }}>(no preview)</div>
              )}
            </Paper>

            {/* Change photo button */}
            <Button
              variant="text"
              component="label"
              size="small"
              sx={{ mt: 1 }}
            >
              Change Photo
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Upload one main photo for your property listing
        </Typography>
      </Grid>
    </>
  );
};

export default FileUploadForm;
